// Global client-side error reporter.
// Wires window.onerror and unhandledrejection to a log endpoint with light
// dedup + throttling so a flapping bug can't flood the table.
import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const recent = new Map<string, number>(); // key -> last sent ts
const DEDUP_WINDOW_MS = 60_000;
const MAX_PER_MINUTE = 10;
let sentInWindow = 0;
let windowStart = Date.now();

function shouldSend(key: string): boolean {
  const now = Date.now();
  if (now - windowStart > 60_000) {
    windowStart = now;
    sentInWindow = 0;
  }
  if (sentInWindow >= MAX_PER_MINUTE) return false;
  const last = recent.get(key) ?? 0;
  if (now - last < DEDUP_WINDOW_MS) return false;
  recent.set(key, now);
  sentInWindow++;
  return true;
}

async function send(payload: Record<string, unknown>) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      apikey: ANON_KEY,
    };
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }
    await fetch(`${SUPABASE_URL}/functions/v1/log-client-error`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Never let the reporter throw.
  }
}

export function reportClientError(input: {
  message: string;
  stack?: string;
  component?: string;
  severity?: "error" | "warning" | "info";
  metadata?: Record<string, unknown>;
}) {
  const key = `${input.severity ?? "error"}|${input.message}|${typeof window !== "undefined" ? window.location.pathname : ""}`;
  if (!shouldSend(key)) return;
  void send({
    ...input,
    url: typeof window !== "undefined" ? window.location.href : undefined,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
  });
}

let installed = false;
export function installErrorReporter() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (e) => {
    const msg = e.message || (e.error && (e.error as Error).message) || "Unknown error";
    // Filter chunk-load flicker we already auto-recover from.
    if (/Failed to fetch dynamically imported module/i.test(msg)) return;
    reportClientError({
      message: msg,
      stack: (e.error as Error | undefined)?.stack,
      metadata: { filename: e.filename, line: e.lineno, col: e.colno },
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    const msg = reason instanceof Error ? reason.message : String(reason ?? "Unhandled rejection");
    reportClientError({
      message: msg,
      stack: reason instanceof Error ? reason.stack : undefined,
      metadata: { kind: "unhandledrejection" },
    });
  });
}
